import { useMemo } from "react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from "recharts";
import { PALETTE, fmt, fmtMoney, KPI } from "./_shared";

export function EmissionsTab({ overview, trend, rows }) {
  const stackData = trend.map((m) => ({
    month: m.month_label,
    "Scope 1 (direct fuel)": m.scope_1_kg,
    "Scope 2 (electricity)": m.scope_2_kg,
    "Scope 3 (supply chain)": m.scope_3_kg,
  }));

  const buAgg = useMemo(() => {
    const map = {};
    for (const r of rows) {
      const k = r.business_unit_code;
      const t = (r.scope_1_kg || 0) + (r.scope_2_kg || 0) + (r.scope_3_kg || 0);
      map[k] = (map[k] || 0) + t;
    }
    return Object.entries(map).map(([name, value]) => ({ name, value: Math.round(value) })).sort((a, b) => b.value - a.value);
  }, [rows]);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI label="Scope 1 – Direct"      value={fmt(overview.emissions.scope_1_kg) + " kg"} hint="Fuel combustion in vehicles/plant"  tone="danger" />
        <KPI label="Scope 2 – Electricity" value={fmt(overview.emissions.scope_2_kg) + " kg"} hint="NSW grid factor 0.68 kg/kWh"       tone="warn"   />
        <KPI label="Scope 3 – Indirect"    value={fmt(overview.emissions.scope_3_kg) + " kg"} hint="Freight, procurement, waste"       tone="default"/>
        <KPI label="Fuel Cost (ex GST)"    value={fmtMoney(overview.emissions.fuel_cost)}     hint={`${fmt(overview.emissions.km_driven)} km driven`} tone="brand" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="rounded-lg border border-slate-200 bg-white p-4 col-span-2">
          <div className="text-sm font-medium text-slate-800 mb-2">Emissions by Scope — Monthly</div>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={stackData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#64748B" }} />
              <YAxis tick={{ fontSize: 11, fill: "#64748B" }} tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} />
              <Tooltip formatter={(v) => `${fmt(v)} kg`} contentStyle={{ fontSize: "12px" }} />
              <Legend wrapperStyle={{ fontSize: "10px" }} />
              <Bar dataKey="Scope 1 (direct fuel)"    stackId="a" fill={PALETTE.s1} />
              <Bar dataKey="Scope 2 (electricity)"   stackId="a" fill={PALETTE.s2} />
              <Bar dataKey="Scope 3 (supply chain)"  stackId="a" fill={PALETTE.s3} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <div className="text-sm font-medium text-slate-800 mb-2">By Business Unit</div>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={buAgg} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
              <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} />
              <YAxis dataKey="name" type="category" width={70} tick={{ fontSize: 10 }} />
              <Tooltip formatter={(v) => `${fmt(v)} kg`} contentStyle={{ fontSize: "12px" }} />
              <Bar dataKey="value" fill={PALETTE.s2} radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
